/**
 * PWA Manifest Service
 * Implements Google's lightweight cross-subdomain PWA strategy:
 * - Single-domain installation with optimized start_url redirects
 * - Lightweight service worker registration
 * - Enhanced browser caching strategy for cross-domain resources
 */

import { SubdomainConfig, isSubdomainAccess } from "../utils/subdomain";

export interface PWAManifestData {
  name: string;
  short_name: string;
  description: string;
  theme_color?: string;
  background_color?: string;
  start_url: string;
  scope: string;
  display: string;
  icons: any[];
  // Enhanced for unified PWA
  id?: string;
  categories?: string[];
  shortcuts?: any[];
  protocol_handlers?: any[];
  launch_handler?: {
    client_mode: string;
  };
  orientation?: string;
  prefer_related_applications?: boolean;
}

/**
 * Generate unified PWA manifest that works across main and subdomain apps
 * Uses a single PWA installation that can handle both main app and whitelabel navigation
 */
export const generatePWAManifest = async (
  options: {
    forceMainManifest?: boolean;
    subdomainConfig?: SubdomainConfig;
  } = {}
): Promise<PWAManifestData> => {
  try {
    const { forceMainManifest = false, subdomainConfig } = options;

    // Always generate unified manifest from main domain perspective to maintain PWA scope
    const baseManifest = await generateUnifiedBaseManifest();

    // If we're on a subdomain and have branding config, enhance the manifest
    if (!forceMainManifest && isSubdomainAccess() && subdomainConfig) {
      return enhanceManifestForSubdomain(baseManifest, subdomainConfig);
    }

    return baseManifest;
  } catch (error) {
    console.error("Error generating PWA manifest:", error);
    return getFallbackManifest();
  }
};

/**
 * Generate the base unified manifest that supports cross-domain navigation
 */
export const generateUnifiedBaseManifest =
  async (): Promise<PWAManifestData> => {
    const isLocalhost = window.location.hostname.includes("localhost");

    // For localhost, use the appropriate local URL
    const baseUrl = isLocalhost
      ? window.location.port === "3001"
        ? "http://localhost:3001"
        : "http://localhost:3000"
      : "https://zurilink.com";

    return {
      id: `${baseUrl}/`,
      name: "ZuriLink",
      short_name: "ZuriLink",
      description: "Connect, collaborate, and grow your verified community",
      theme_color: "#0f172a",
      background_color: "#0f172a",
      // Google's recommendation: Use redirect URL as start_url for cross-domain handling
      start_url: `${baseUrl}/pwa-redirect?utm_source=pwa&utm_medium=homescreen`,
      // Optimize scope for main domain only (Google's single-domain strategy)
      scope: `${baseUrl}/`,
      display: "standalone",
      icons: await generatePWAIcons(),
      categories: ["social", "networking", "productivity"],
      shortcuts: await generateAppShortcuts(baseUrl),
      // Enhanced protocol handlers for better cross-domain experience
      protocol_handlers: [
        {
          protocol: "web+zurilink",
          url: `${baseUrl}/pwa-redirect?target=%s`,
        },
      ],
      launch_handler: {
        client_mode: "navigate-existing",
      },
      // Google's recommendation: Optimize for installation
      orientation: "portrait-primary",
      prefer_related_applications: false,
    };
  };

/**
 * Enhance manifest for subdomain branding while maintaining unified scope
 */
export const enhanceManifestForSubdomain = async (
  baseManifest: PWAManifestData,
  subdomainConfig: SubdomainConfig
): Promise<PWAManifestData> => {
  const brandedName =
    subdomainConfig.brandName || subdomainConfig.affiliationName || "ZuriLink";

  return {
    ...baseManifest,
    name: `${brandedName} - ZuriLink`,
    short_name: brandedName,
    description:
      subdomainConfig.description ||
      `${brandedName} community - Powered by ZuriLink`,
    theme_color: subdomainConfig.primaryColor || baseManifest.theme_color,
    background_color:
      subdomainConfig.primaryColor || baseManifest.background_color,
    // Keep the unified scope to maintain PWA context
    icons: await generatePWAIcons(subdomainConfig),
  };
};

/**
 * Generate app shortcuts for the unified PWA
 */
export const generateAppShortcuts = async (baseUrl: string) => {
  return [
    {
      name: "Communities",
      short_name: "Communities",
      description: "Browse and join communities",
      url: `${baseUrl}/communities`,
      icons: [
        {
          src: "/android-chrome-192x192.png",
          sizes: "192x192",
          type: "image/png",
        },
      ],
    },
    {
      name: "Messages",
      short_name: "Messages",
      description: "View your messages",
      url: `${baseUrl}/messages`,
      icons: [
        {
          src: "/android-chrome-192x192.png",
          sizes: "192x192",
          type: "image/png",
        },
      ],
    },
    {
      name: "Profile",
      short_name: "Profile",
      description: "View your profile",
      url: `${baseUrl}/profile`,
      icons: [
        {
          src: "/android-chrome-192x192.png",
          sizes: "192x192",
          type: "image/png",
        },
      ],
    },
  ];
};

/**
 * Get fallback manifest in case of errors
 */
export const getFallbackManifest = (): PWAManifestData => {
  const baseUrl = window.location.origin;

  return {
    name: "ZuriLink",
    short_name: "ZuriLink",
    description: "Connect, collaborate, and grow your verified community",
    theme_color: "#0f172a",
    background_color: "#0f172a",
    start_url: `${baseUrl}/?utm_source=pwa`,
    scope: `${baseUrl}/`,
    display: "standalone",
    icons: [],
    launch_handler: {
      client_mode: "navigate-existing",
    },
  };
};

/**
 * Generate PWA icons based on configuration
 */
export const generatePWAIcons = async (_subdomainConfig?: SubdomainConfig) => {
  const baseIcons = [
    {
      src: "/android-chrome-192x192.png",
      sizes: "192x192",
      type: "image/png",
      purpose: "any",
    },
    {
      src: "/android-chrome-512x512.png",
      sizes: "512x512",
      type: "image/png",
      purpose: "any",
    },
    {
      src: "/maskable-icon-192x192.png",
      sizes: "192x192",
      type: "image/png",
      purpose: "maskable",
    },
    {
      src: "/maskable-icon-512x512.png",
      sizes: "512x512",
      type: "image/png",
      purpose: "maskable",
    },
    {
      src: "/apple-touch-icon.png",
      sizes: "180x180",
      type: "image/png",
      purpose: "any",
    },
  ];

  // If subdomain has custom logo, we could potentially use it for PWA icons
  // For now, return standard icons (could be enhanced to generate partner-branded icons)
  return baseIcons;
};

/**
 * Update the PWA manifest dynamically while preserving unified scope
 */
export const updatePWAManifest = async (
  options: {
    subdomainConfig?: SubdomainConfig;
    forceMainManifest?: boolean;
  } = {}
) => {
  try {
    const manifestData = await generatePWAManifest(options);

    // Update the manifest link in the document head
    let manifestLink = document.querySelector(
      'link[rel="manifest"]'
    ) as HTMLLinkElement;

    if (!manifestLink) {
      manifestLink = document.createElement("link");
      manifestLink.rel = "manifest";
      document.head.appendChild(manifestLink);
    }

    // Create a blob URL with the dynamic manifest
    const manifestBlob = new Blob([JSON.stringify(manifestData, null, 2)], {
      type: "application/json",
    });

    // Clean up previous blob URL to prevent memory leaks
    if (manifestLink.href && manifestLink.href.startsWith("blob:")) {
      URL.revokeObjectURL(manifestLink.href);
    }

    const manifestUrl = URL.createObjectURL(manifestBlob);
    manifestLink.href = manifestUrl;

    // Update theme color meta tag
    let themeColorMeta = document.querySelector(
      'meta[name="theme-color"]'
    ) as HTMLMetaElement;
    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.name = "theme-color";
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.content = manifestData.theme_color || "#0f172a";

    // Update app title
    document.title = manifestData.name;

    // Add meta tag to indicate unified PWA
    let unifiedPWAMeta = document.querySelector(
      'meta[name="zurilink-unified-pwa"]'
    ) as HTMLMetaElement;
    if (!unifiedPWAMeta) {
      unifiedPWAMeta = document.createElement("meta");
      unifiedPWAMeta.name = "zurilink-unified-pwa";
      unifiedPWAMeta.content = "true";
      document.head.appendChild(unifiedPWAMeta);
    }

    // Store manifest data for potential service worker usage
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: "MANIFEST_UPDATED",
        manifestData,
      });
    }

    return manifestData;
  } catch (error) {
    console.error("Error updating PWA manifest:", error);
    throw error;
  }
};

/**
 * Initialize unified PWA manifest on app startup with installation context tracking
 */
export const initializeUnifiedPWA = async (
  subdomainConfig?: SubdomainConfig
) => {
  try {
    // Import installation context manager
    const { PWAInstallationManager } = await import(
      "../utils/pwaInstallationContext"
    );

    // Auto-detect and record installation context if needed
    PWAInstallationManager.autoDetectInstallation();

    // Update installation context with subdomain info if available
    if (subdomainConfig) {
      PWAInstallationManager.updateInstallationContext({
        affiliationId: subdomainConfig.affiliationId,
        affiliationName:
          subdomainConfig.affiliationName || subdomainConfig.brandName,
      });
    }

    // Update manifest based on current context
    await updatePWAManifest({ subdomainConfig });

    // Cache subdomain config if provided
    if (subdomainConfig) {
      await cacheSubdomainConfig(subdomainConfig);
    }

    // PWA initialization completed successfully
  } catch (error) {
    console.error("Failed to initialize unified PWA:", error);
  }
};

/**
 * Cache subdomain configuration for offline PWA access
 */
export const cacheSubdomainConfig = async (config: SubdomainConfig) => {
  try {
    if ("caches" in window) {
      const cache = await caches.open("subdomain-config-v1");
      const configResponse = new Response(JSON.stringify(config));
      await cache.put("/subdomain-config/current", configResponse);
    }
  } catch (error) {
    console.error("Error caching subdomain config:", error);
  }
};

/**
 * Get cached subdomain configuration for offline access
 */
export const getCachedSubdomainConfig =
  async (): Promise<SubdomainConfig | null> => {
    try {
      if ("caches" in window) {
        const cache = await caches.open("subdomain-config-v1");
        const response = await cache.match("/subdomain-config/current");
        if (response) {
          return await response.json();
        }
      }
    } catch (error) {
      console.error("Error getting cached subdomain config:", error);
    }
    return null;
  };
